/**
 * Facebook Page - Feed Webhook Types
 *
 * These types represent the webhook event structure for feed events.
 * Triggered when there are changes to a Page's feed, such as Posts, shares,
 * likes, comments, etc. Webhooks are not sent for Ad Posts, but are sent
 * for Comments on Ad Posts. Notifications for Page likes will only be sent
 * for Pages that have fewer than 10K likes.
 *
 * @see https://developers.facebook.com/docs/graph-api/webhooks/reference/page/#feed
 */

import { PageWebhookPayload } from './base-types';

/**
 * Enumeration of item types in feed events
 */
export enum FeedItemType {
  ALBUM = 'album',
  ADDRESS = 'address',
  COMMENT = 'comment',
  CONNECTION = 'connection',
  COUPON = 'coupon',
  EVENT = 'event',
  EXPERIENCE = 'experience',
  GROUP = 'group',
  GROUP_MESSAGE = 'group_message',
  INTEREST = 'interest',
  LINK = 'link',
  MENTION = 'mention',
  MILESTONE = 'milestone',
  NOTE = 'note',
  PAGE = 'page',
  PICTURE = 'picture',
  PLATFORM_STORY = 'platform-story',
  PHOTO = 'photo',
  PHOTO_ALBUM = 'photo-album',
  POST = 'post',
  PROFILE = 'profile',
  QUESTION = 'question',
  RATING = 'rating',
  REACTION = 'reaction',
  RELATIONSHIP_STATUS = 'relationship-status',
  SHARE = 'share',
  STATUS = 'status',
  STORY = 'story',
  TIMELINE_COVER = 'timeline cover',
  TAG = 'tag',
  VIDEO = 'video',
}

/**
 * Enumeration of action verbs in feed events
 */
export enum FeedActionVerb {
  ADD = 'add',
  BLOCK = 'block',
  EDIT = 'edit',
  EDITED = 'edited',
  DELETE = 'delete',
  FOLLOW = 'follow',
  HIDE = 'hide',
  MUTE = 'mute',
  REMOVE = 'remove',
  UNBLOCK = 'unblock',
  UNHIDE = 'unhide',
  UPDATE = 'update',
}

/**
 * Sender information in feed events
 */
export interface FeedSender {
  /** The ID of the sender */
  id: string;

  /** The name of the sender */
  name?: string;
}

/**
 * Page post additional information
 */
export interface FeedPagePost {
  /** Type of the post (e.g., photo, video) */
  type?: string;

  /** Status type (e.g., added_photos) */
  status_type?: string;

  /** Whether the post is published */
  is_published?: boolean;

  /** Timestamp of when the post was last updated */
  updated_time?: string;

  /** Permanent static URL to the post */
  permalink_url?: string;

  /** Promotion status (e.g., inactive, extendable) */
  promotion_status?: string;
}

/**
 * Feed event value data
 */
export interface FeedEventValue {
  /** Edited time (datetime) */
  edited_time?: string;

  /** The sender information */
  from?: FeedSender;

  /** Additional post content information */
  post?: FeedPagePost;

  /** Description of the type of a status update */
  status_type?: string;

  /** Whether a scheduled post was published */
  is_published?: boolean;

  /** The time the post was last updated */
  updated_time?: string;

  /** The permanent static URL to the post on facebook.com */
  permalink_url?: string;

  /** Whether the post is hidden or not */
  is_hidden?: boolean;

  /** The link to attached content */
  link?: string;

  /** The message that is part of the content */
  message?: string;

  /** The link to an attached photo */
  photo?: string;

  /** The IDs of the photos that were added to an album */
  photo_ids?: string[];

  /** The links to any attached photos */
  photos?: string[];

  /** The post ID */
  post_id?: string;

  /** The story—only logged for a milestone item */
  story?: string;

  /** The title—only logged for a milestone item */
  title?: string;

  /** The link to an attached video */
  video?: string;

  /** The code why the video is flagged */
  video_flag_reason?: number;

  /** The type of action taken */
  action?: string;

  /** The album ID */
  album_id?: string;

  /** The comment ID */
  comment_id?: string;

  /** The timestamp of when the object was created */
  created_time?: string;

  /** The ID of the event, if the feed post is an event creation story */
  event_id?: string;

  /** The type of item */
  item?: FeedItemType;

  /** The ID of the open graph object */
  open_graph_story_id?: string;

  /** The parent ID */
  parent_id?: string;

  /** The photo ID */
  photo_id?: string;

  /** The type of the user reaction */
  reaction_type?: string;

  /** Whether the post is published */
  published?: number;

  /** The recipient ID */
  recipient_id?: string;

  /** The share ID */
  share_id?: string;

  /** The type of action taken */
  verb?: FeedActionVerb;

  /** The video ID */
  video_id?: string;
}

/**
 * Main webhook event structure for feed events
 *
 * This event is triggered when there are changes to a Page's feed.
 * The webhook provides information about posts, comments, likes, shares, etc.
 *
 * @example Post created:
 * ```json
 * {
 *   "field": "feed",
 *   "value": {
 *     "from": {
 *       "id": "123456789",
 *       "name": "Page Name"
 *     },
 *     "post_id": "123456789_987654321",
 *     "verb": "add",
 *     "item": "post",
 *     "created_time": "1234567890",
 *     "message": "Hello World!"
 *   }
 * }
 * ```
 */
export interface FeedWebhookEvent {
  /** Name of the updated field */
  field: 'feed';

  /** The contents of the update */
  value: FeedEventValue;
}

/**
 * Complete webhook payload structure for feed events
 */
export interface FeedWebhookPayload extends PageWebhookPayload<FeedWebhookEvent> {}

/**
 * Type guard to check if a webhook event is a feed event
 *
 * @param event - The webhook event to check
 * @returns True if the event is a feed event
 *
 * @example
 * ```typescript
 * if (isFeedEvent(event)) {
 *   console.log(`Feed event: ${event.value.verb} ${event.value.item}`);
 * }
 * ```
 */
export function isFeedEvent(event: any): event is FeedWebhookEvent {
  return event && typeof event === 'object' && event.field === 'feed';
}

/**
 * Type guard to check if a feed event is a post creation
 *
 * @param value - The feed event value to check
 * @returns True if the event is a post creation
 */
export function isPostCreated(value: FeedEventValue): boolean {
  return value.verb === FeedActionVerb.ADD && value.item === FeedItemType.POST;
}

/**
 * Type guard to check if a feed event is a comment
 *
 * @param value - The feed event value to check
 * @returns True if the event is a comment
 */
export function isComment(value: FeedEventValue): boolean {
  return value.item === FeedItemType.COMMENT;
}

/**
 * Type guard to check if a feed event is a photo
 *
 * @param value - The feed event value to check
 * @returns True if the event is a photo
 */
export function isPhoto(value: FeedEventValue): boolean {
  return value.item === FeedItemType.PHOTO || value.item === FeedItemType.PHOTO_ALBUM;
}

/**
 * Type guard to check if a feed event is a video
 *
 * @param value - The feed event value to check
 * @returns True if the event is a video
 */
export function isVideo(value: FeedEventValue): boolean {
  return value.item === FeedItemType.VIDEO;
}

/**
 * Type guard to check if a feed event is a reaction
 *
 * @param value - The feed event value to check
 * @returns True if the event is a reaction
 */
export function isReaction(value: FeedEventValue): boolean {
  return value.item === FeedItemType.REACTION;
}

/**
 * Type guard to check if a feed event has a message
 *
 * @param value - The feed event value to check
 * @returns True if the event has a message
 */
export function hasMessage(value: FeedEventValue): value is FeedEventValue & { message: string } {
  return typeof value.message === 'string' && value.message.length > 0;
}

/**
 * Utility type for extracting just the feed event data
 */
export type FeedEventData = FeedEventValue;

/**
 * Processing context for feed events
 */
export interface FeedProcessingContext {
  /** The page ID */
  pageId: string;

  /** When the event occurred */
  timestamp: number;

  /** Human-readable datetime for the event timestamp */
  eventDate: Date;

  /** The sender information */
  sender?: FeedSender;

  /** Post ID if available */
  postId?: string;

  /** Comment ID if available */
  commentId?: string;

  /** The action verb */
  verb?: FeedActionVerb;

  /** The item type */
  item?: FeedItemType;

  /** Message content if available */
  message?: string;

  /** Whether this is a post creation */
  isPostCreated: boolean;

  /** Whether this is a comment */
  isComment: boolean;

  /** Whether this is a photo */
  isPhoto: boolean;

  /** Whether this is a video */
  isVideo: boolean;

  /** Whether this is a reaction */
  isReaction: boolean;
}

/**
 * Helper function to extract processing context from a feed event
 *
 * @param pageId - The page ID from the webhook entry
 * @param timestamp - The timestamp from the webhook entry
 * @param event - The feed webhook event
 * @returns Simplified processing context
 *
 * @example
 * ```typescript
 * const context = extractFeedContext(entry.id, entry.time, event);
 * console.log(`Feed event: ${context.verb} ${context.item}`);
 * if (context.isPostCreated) {
 *   console.log(`New post created: ${context.message}`);
 * }
 * ```
 */
export function extractFeedContext(
  pageId: string,
  timestamp: number,
  event: FeedWebhookEvent
): FeedProcessingContext {
  const { value } = event;

  return {
    pageId,
    timestamp,
    eventDate: new Date(timestamp),
    sender: value.from,
    postId: value.post_id,
    commentId: value.comment_id,
    verb: value.verb,
    item: value.item,
    message: value.message,
    isPostCreated: isPostCreated(value),
    isComment: isComment(value),
    isPhoto: isPhoto(value),
    isVideo: isVideo(value),
    isReaction: isReaction(value),
  };
}

/**
 * Helper function to extract all photos from a feed event
 *
 * @param value - The feed event value
 * @returns Array of photo URLs and IDs
 *
 * @example
 * ```typescript
 * const photos = extractPhotos(event.value);
 * console.log(`Found ${photos.length} photo(s)`);
 * ```
 */
export function extractPhotos(value: FeedEventValue): Array<{ url?: string; id?: string }> {
  const photos: Array<{ url?: string; id?: string }> = [];

  if (value.photo) {
    photos.push({ url: value.photo, id: value.photo_id });
  }

  if (value.photos && Array.isArray(value.photos)) {
    photos.push(...value.photos.map((url, index) => ({
      url,
      id: value.photo_ids?.[index],
    })));
  }

  return photos;
}

/**
 * Constants related to feed events
 */
export const FEED_CONSTANTS = {
  /** Webhook field name */
  FIELD_NAME: 'feed' as const,

  /** Maximum Page likes threshold for notifications */
  MAX_PAGE_LIKES_FOR_NOTIFICATIONS: 10000,
} as const;
